import { ApplicationConfig, provideBrowserGlobalErrorListeners, provideZonelessChangeDetection, APP_INITIALIZER } from '@angular/core';
import { provideRouter, Router, UrlTree, NavigationEnd, NavigationError, TitleStrategy } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { provideNativeDateAdapter } from '@angular/material/core';
import { provideNgxMask } from 'ngx-mask';

import { routes } from './app.routes';
import { ThemeService } from '../@backoffice/services/theme.service';
import { SnackbarService } from '../@backoffice/services/snackbar.service';
import { IconService } from './core/services/icon.service';
import { AppTitleStrategy } from './core/services/app-title.strategy';
import { clientIdInterceptor } from './core/interceptors/http.interceptor';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { responseUnwrapInterceptor } from './core/interceptors/response-unwrap.interceptor';
import { buildClientPath, buildClientRoute, getCurrentClientId, loadClientId } from './core/utils/client-route.util';

/**
 * Initialize icons on application startup
 */
function initializeIcons(iconService: IconService) {
  return () => {
    iconService.registerIcons();
  };
}

/**
 * Patch router navigation methods to preserve /:clientId prefix.
 */
function initializeClientAwareRouter(router: Router) {
  return () => {
    const originalNavigate = router.navigate.bind(router);
    const originalNavigateByUrl = router.navigateByUrl.bind(router);

    router.navigate = ((commands: readonly unknown[], extras?: Parameters<Router['navigate']>[1]) => {
      const clientAwareCommands = buildClientRoute(router, [...commands]);
      return originalNavigate(clientAwareCommands as Parameters<Router['navigate']>[0], extras);
    }) as Router['navigate'];

    router.navigateByUrl = ((url: string | UrlTree, extras?: Parameters<Router['navigateByUrl']>[1]) => {
      // RouterLink calls navigateByUrl with a UrlTree, not a string — still apply client prefix.
      const serialized = typeof url === 'string' ? url : router.serializeUrl(url);
      return originalNavigateByUrl(buildClientPath(router, serialized), extras);
    }) as Router['navigateByUrl'];

    if (typeof window !== 'undefined') {
      const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      const clientIdFromUrl = getCurrentClientId(router);

      if (!clientIdFromUrl) {
        // No clientId detectable from the URL — check localStorage first.
        // This handles the case where the user opens the app at a bare path
        // (e.g. /dashboard or /) after previously being authenticated.
        const storedClientId = loadClientId();
        if (storedClientId) {
          const pathPart = window.location.pathname;
          const queryAndHash = window.location.search + window.location.hash;
          const pathSegments = pathPart.split('/').filter(Boolean);

          // Preserve the original path so /admin-settings/overview becomes
          // /:clientId/admin-settings/overview instead of always /dashboard.
          const targetUrl = pathSegments.length > 0
            ? `/${storedClientId}${pathPart}${queryAndHash}`
            : `/${storedClientId}/dashboard`;

          return originalNavigateByUrl(targetUrl, { replaceUrl: true });
        }
      } else {
        // clientId is in the URL — ensure it is properly prefixed.
        const clientAwareUrl = buildClientPath(router, currentUrl);
        if (clientAwareUrl !== currentUrl) {
          return originalNavigateByUrl(clientAwareUrl, { replaceUrl: true });
        }
      }
    }

    return Promise.resolve(true);
  };
}

/**
 * Recover from stale lazy-loaded chunk errors by reloading once.
 *
 * Lazy chunks (loadComponent / loadChildren) fail with "Failed to fetch dynamically
 * imported module" when the browser holds a reference to a hash that is no longer on
 * disk — common after a redeploy with the tab open, or after a dev rebuild. A fresh
 * index.html resolves it.
 *
 * The `chunkReloadAttempted` sessionStorage flag prevents a refresh loop: if the
 * second load still fails (build genuinely broken), we log and let the error surface
 * instead of reloading again. The flag is cleared on the first successful navigation,
 * so future incidents in the same tab still get their one retry.
 *
 * Shared with verify-otp.component.ts — keep keys in sync.
 */
const CHUNK_RELOAD_FLAG = 'chunkReloadAttempted';
const CHUNK_ERROR_PATTERN = /Failed to fetch dynamically imported module|ChunkLoadError|Loading chunk|Importing a module script failed/i;

function initializeChunkLoadRecovery(router: Router) {
  return () => {
    if (typeof window === 'undefined') {
      return;
    }

    router.events.subscribe((event) => {
      if (event instanceof NavigationError) {
        const message = String((event.error as { message?: unknown })?.message ?? event.error ?? '');
        if (!CHUNK_ERROR_PATTERN.test(message)) {
          return;
        }
        let alreadyAttempted = false;
        try {
          alreadyAttempted = !!sessionStorage.getItem(CHUNK_RELOAD_FLAG);
        } catch {
          // sessionStorage unavailable (private mode / quota) — skip the retry entirely
          // rather than risk a loop without a way to mark our attempt.
          return;
        }
        if (alreadyAttempted) {
          console.error('[ChunkLoadRecovery] Reload already attempted; not retrying to avoid a loop.', event.error);
          return;
        }
        try {
          sessionStorage.setItem(CHUNK_RELOAD_FLAG, String(Date.now()));
        } catch {
          return;
        }
        console.warn('[ChunkLoadRecovery] Stale chunk detected; reloading to fetch a fresh bundle.', event.error);
        // Navigate to the URL the user actually wanted, not the page they happened to be
        // on. event.url is the attempted destination; reload() would strand them on the
        // previous route and force a second click. Fall back to reload() if url is missing.
        if (event.url) {
          window.location.assign(event.url);
        } else {
          window.location.reload();
        }
      } else if (event instanceof NavigationEnd) {
        try {
          sessionStorage.removeItem(CHUNK_RELOAD_FLAG);
        } catch {
          // best-effort cleanup
        }
      }
    });
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(routes),
    { provide: TitleStrategy, useClass: AppTitleStrategy },
    provideAnimations(),
    provideNativeDateAdapter(),
    // Interceptor order: request interceptors first, response interceptor last
    // Order: authInterceptor (adds headers) -> clientIdInterceptor (adds headers) -> responseUnwrapInterceptor (processes response)
    provideHttpClient(withInterceptors([
      authInterceptor,           // Adds Authorization & Accept-Language headers
      clientIdInterceptor,        // Adds X-Client-Id header
      responseUnwrapInterceptor   // Unwraps BaseApiResponse (processes responses)
    ])),
    {
      provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
      useValue: {
        duration: 5000,
        horizontalPosition: 'right',
        verticalPosition: 'bottom'
      }
    },
    ThemeService,
    SnackbarService,
    IconService,
    provideNgxMask({
      // Ensure 'A' is strictly alphabetic and '0' is numeric
      patterns: {
        A: { pattern: /[A-Za-z]/ },
        0: { pattern: /[0-9]/ }
      }
    }),
    {
      provide: APP_INITIALIZER,
      useFactory: initializeIcons,
      deps: [IconService],
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeClientAwareRouter,
      deps: [Router],
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeChunkLoadRecovery,
      deps: [Router],
      multi: true
    }
  ]
};
